import React, { useState, useEffect, useContext } from "react";
import { Grid, Typography, makeStyles } from "@material-ui/core";
import MButton from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import { recordTypes } from "../../API/config";
import {
  getDocumentTypes,
  getEntityDocuments,
  removeEntityDocument,
} from "../../API/Entity";
import { httpGetF } from "../../API/Http";
import { LoadingIndicator, NoAccessScreen } from "../../Component";
import ModalDialogContext from "../../Context/ModalDialog/ModalDialogContext";
import CheckBox from "../../Component/Forms/CheckboxWithLabel";
import DocumentsTable from "./Components/DocumentsTable";
import styles from "./styles";
import mime from "mime-types";
import { saveAs } from "file-saver";
import UploadFilesDialog from "./Components/UploadFilesDialog";
import FilePreviewDialog from "./Components/FilePreviewDialog";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

const useStyles = makeStyles(styles);

const EndityDocuments = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    entity,
    category,
    onDocumentUpdate,
    reloadEntity,
    hasRecordPermission,
  } = props;
  const { id: entityId, urn } = entity || {};

  const [documents, setDocuments] = useState([]);

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [document, setDocument] = useState();
  const [isLoading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [hideExpired, setHideExpired] = useState(true);

  const {
    showMessage,
    showErrorMessage,
    showConfirmation,
    showLoadingMessage,
    hideModalDialog,
  } = useContext(ModalDialogContext);

  const [canViewDocuments, setCanViewDocuments] = useState();
  const [canAddDocument, setCanAddDocument] = useState();
  const [canRemoveDocument, setCanRemoveDocument] = useState();
  const [canViewDocumentContents, setCanViewDocumentContents] = useState();
  const [canUpdateDocumentName, setCanUpdateDocumentName] = useState();

  useEffect(() => {
    let isCancelled = false;

    const setPermissions = async () => {
      const { permissions = {} } = entity;
      const canUpdateRecord = hasRecordPermission(
        permissions,
        recordTypes.record.actions.update.name,
      );
      const canViewDocuments = hasRecordPermission(
        permissions,
        category === "AML"
          ? recordTypes.record.actions.viewAmlDocuments.name
          : recordTypes.record.actions.viewKycDocuments.name,
      );
      setCanViewDocuments(canViewDocuments);
      if (canUpdateRecord) {
        const canAddDocument = hasRecordPermission(
          permissions,
          category === "AML"
            ? recordTypes.record.actions.addAmlDocument.name
            : recordTypes.record.actions.addKycDocument.name,
        );
        setCanAddDocument(canAddDocument);
        const canRemoveDocument = hasRecordPermission(
          permissions,
          category === "AML"
            ? recordTypes.record.actions.removeAmlDocument.name
            : recordTypes.record.actions.removeKycDocument.name,
        );
        setCanRemoveDocument(canRemoveDocument);

        const canUpdateDocumentName = hasRecordPermission(
          permissions,
          category === "AML"
            ? recordTypes.record.actions.updateAmlDocumentName.name
            : recordTypes.record.actions.updateKycDocumentName.name,
        );
        setCanUpdateDocumentName(canUpdateDocumentName);
      }

      const canViewDocumentContents = hasRecordPermission(
        permissions,
        category === "AML"
          ? recordTypes.record.actions.viewAmlDocumentContents.name
          : recordTypes.record.actions.viewKycDocumentContents.name,
      );
      setCanViewDocumentContents(canViewDocumentContents);
    };

    const loadDocumentTypes = async () => {
      setLoading(true);
      try {
        const results = await getDocumentTypes(category);
        const { data = [] } = results;

        if (!isCancelled) {
          setDocumentTypes(
            data.map((dt) => {
              return {
                value: dt.id,
                label: dt.description,
              };
            }),
          );
          setLoading(false);
        }
      } catch (error) {
        showErrorMessage({ error });
        setLoading(false);
      }
    };

    loadDocumentTypes();
    setPermissions();
    return () => {
      isCancelled = true;
    };
  }, [entity]);

  useEffect(() => {
    let isCancelled = false;
    const loadDocuments = async () => {
      try {
        setLoading(true);
        const result = await getEntityDocuments(entityId, category);
        const { data = [] } = result || {};
        const documents = data.filter((d) => d.category === category);
        if (!isCancelled) {
          setDocuments(documents);
        }
      } catch (error) {
        showErrorMessage({ error });
      }
      if (!isCancelled) {
        setLoading(false);
        setReload(false);
      }
    };
    if (reload) {
      loadDocuments();
    }
    return () => {
      isCancelled = true;
    };
  }, [reload]);

  useEffect(() => {
    setReload(true);
  }, []);

  if (!canViewDocuments) {
    return <NoAccessScreen />;
  }

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const openFileUploadDialog = () => {
    setIsUploadDialogOpen(true);
  };

  const closeFileUploadDialog = (load) => {
    if (load) {
      reloadEntity();
    }
    setReload(load);
    setIsUploadDialogOpen(false);
  };

  const openViewDialog = async (document) => {
    setDocument(document);
    setIsViewDialogOpen(true);
  };

  const closeViewDialog = () => {
    setDocument();
    setIsViewDialogOpen(false);
  };

  const downloadDocument = async (documentId) => {
    const docEnt = documents.find((x) => x.id === documentId);
    showLoadingMessage();
    try {
      const response = await httpGetF("/entity/document", {
        documentId: documentId,
        entityId: entityId,
        category: "AML",
      });
      const file = new Blob([response], { type: mime.lookup(docEnt.filename) });
      hideModalDialog();
      saveAs(file, docEnt.filename);
    } catch (e) {
      showErrorMessage({
        message: t("messages.record.documents.couldNotFetchDocument"),
      });
    }
  };

  const removeDocument = async (documentId) => {
    showConfirmation({
      message: t("messages.record.documents.deleteDoccument"),
      onPressYes: async (event) => {
        setLoading(true);
        try {
          const result = await removeEntityDocument(documentId, entityId);
          const { message } = result;
          if (onDocumentUpdate) {
            await onDocumentUpdate();
          }
          showMessage({ message });
          if (reloadEntity) {
            reloadEntity();
          }
        } catch (error) {
          showErrorMessage({ error });
        }
        setReload(true);
      },
    });
  };

  const hideExpiredChange = async (e) => {
    setHideExpired(e.target.checked);
  };

  return (
    <>
      <div className={classes.documentsBarContainer}>
        <Grid container direction="row" spacing={1}>
          <Grid item xs={6} container justify="flex-start" alignItems="center">
            <Typography component="span" className={classes.documentsBarTitle}>
              {t("labels.record.documents.sectionHeader")}
            </Typography>
          </Grid>
          <Grid item xs={6} container justify="flex-end" alignItems="center">
            <CheckBox
              label={t("labels.record.documents.hideExpired")}
              onChange={hideExpiredChange}
              value={hideExpired}
            />
            {canAddDocument && (
              <MButton
                color="primary"
                variant="contained"
                label={t("labels.common.add")}
                style={{ height: 30, marginLeft: 10 }}
                onClick={openFileUploadDialog}
              >
                <AddIcon />
              </MButton>
            )}
          </Grid>
        </Grid>
      </div>
      <div className={classes.documentsTableContainer}>
        {isLoading ? (
          renderLoadingIndicator()
        ) : (
          <DocumentsTable
            data={documents}
            category={category}
            hideExpired={hideExpired}
            onRowViewClick={openViewDialog}
            onRowDownloadClick={downloadDocument}
            canViewDocumentContents={canViewDocumentContents}
            canRemoveDocument={canRemoveDocument}
            removeDocument={removeDocument}
            canUpdateDocumentName={canUpdateDocumentName}
          />
        )}
      </div>

      {isUploadDialogOpen && (
        <UploadFilesDialog
          isDialogOpen={isUploadDialogOpen}
          documentTypes={documentTypes}
          entityId={entityId}
          category={category}
          urn={urn}
          onClose={closeFileUploadDialog}
          onSubmit={() => closeFileUploadDialog(true)}
        ></UploadFilesDialog>
      )}
      {isViewDialogOpen && (
        <FilePreviewDialog
          isDialogOpen={isViewDialogOpen}
          entityId={entityId}
          document={document}
          urn={urn}
          onClose={closeViewDialog}
        />
      )}
    </>
  );
};

export default EndityDocuments;
